import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AdHocAggregateResult, AnalyticsField, AnalyticsFilter, AnalyticsMetric } from '@/shared/analytics/AnalyticsTypes';
import { UsageCategoryTaxonomyService } from '@/features/usage-categories/UsageCategoryTaxonomyService';
import { loadTaxonomyOnce, rootAncestorByLabel, usageCategoryFor } from '@/features/usage-categories/UsageTaxonomyLookup';

const BREAKDOWN_METRICS: AnalyticsMetric[] = ['turns', 'sessions', 'users', 'totalTokens', 'avgConfidence', 'allocatedCostUsd'];
const BREAKDOWN_ROW_LIMIT = 500;
const CHILD_ROW_LIMIT = 1000;

export interface UseCaseBreakdownRow {
    value: string;
    /** The usage-category taxonomy root this value belongs to, or null when unmapped. */
    parent: string | null;
    /** The immediate parent value (e.g. the category a subcategory belongs to), or null for a top-level row. */
    parentValue: string | null;
    turns: number;
    sessions: number;
    users: number;
    /** Total tokens (input + output) across this value's interactions. */
    totalTokens: number;
    /** Allocated real spend ($) across this value's interactions; 0 when no cost data covers it. */
    allocatedCostUsd: number;
    /** Average classifier confidence for this value, 0–1. */
    confidence: number;
    /** This row's share of total tokens, 0–1. */
    share: number;
    /** Bar width vs the peak row by tokens, 0–100. */
    widthPct: number;
    /** Inline nested rows (e.g. a category's subcategories), when preloaded via `childField`. */
    children?: UseCaseBreakdownRow[];
}

/**
 * Backs the Use Cases list and the category page's Subcategories tab: prompt
 * volume per task category (or subcategory), joined to the usage-category
 * taxonomy for each value's root parent.
 */
@injectable()
export class UseCaseBreakdownViewModel extends BaseViewModel {
    rows: UseCaseBreakdownRow[] = [];
    /** The raw aggregate backing the rows — feeds the summary and insights panels. */
    primary: AdHocAggregateResult | null = null;
    loading = false;
    errorMessage: string | null = null;

    constructor(
        @inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway,
        @inject(UsageCategoryTaxonomyService) private taxonomy: UsageCategoryTaxonomyService,
    ) {
        super();
    }

    async load(field: AnalyticsField, filters: AnalyticsFilter[], withParents: boolean, childField?: AnalyticsField): Promise<void> {
        this.rows = [];
        this.primary = null;
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const [result, childResult, taxonomy] = await Promise.all([
                this.gateway.aggregate({
                    source: 'interactions',
                    groupBy: [field],
                    metrics: BREAKDOWN_METRICS,
                    orderBy: { metric: 'turns', direction: 'desc' },
                    limit: BREAKDOWN_ROW_LIMIT,
                    filters,
                }),
                childField === undefined ? Promise.resolve(null) : (
                    this.gateway.aggregate({
                        source: 'interactions',
                        groupBy: [field, childField],
                        metrics: BREAKDOWN_METRICS,
                        orderBy: { metric: 'turns', direction: 'desc' },
                        limit: CHILD_ROW_LIMIT,
                        filters,
                    })
                ),
                withParents ? loadTaxonomyOnce(this.taxonomy) : Promise.resolve(null),
            ]);
            const lookup = taxonomy === null ? null : rootAncestorByLabel(taxonomy.nodes);
            this.primary = result;
            const totalTokens = result.totals.totalTokens;
            const childrenByParent = childField === undefined || childResult === null ? null : this.groupChildren(field, childField, childResult);
            this.rows = result.rows.map((row) => {
                const value = row.keys[field] ?? '—';
                const share = totalTokens > 0 ? row.metrics.totalTokens / totalTokens : 0;
                return {
                    value,
                    parent: lookup === null ? null : usageCategoryFor(lookup, value),
                    parentValue: null,
                    turns: row.metrics.turns,
                    sessions: row.metrics.sessions,
                    users: row.metrics.users,
                    totalTokens: row.metrics.totalTokens,
                    allocatedCostUsd: row.metrics.allocatedCostUsd,
                    confidence: row.metrics.avgConfidence,
                    share,
                    widthPct: share > 0 ? Math.max(2, Math.round(share * 100)) : 0,
                    children: childrenByParent?.get(value),
                };
            });
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    /**
     * Builds child rows grouped by their parent value. Each child's `share` — and
     * its bar `widthPct` — is its fraction of its parent group's tokens, so the bar
     * width matches the share readout.
     */
    private groupChildren(field: AnalyticsField, childField: AnalyticsField, result: AdHocAggregateResult): Map<string, UseCaseBreakdownRow[]> {
        const grouped = new Map<string, AdHocAggregateResult['rows']>();
        for (const row of result.rows) {
            const parentValue = row.keys[field] ?? '—';
            const bucket = grouped.get(parentValue);
            if (bucket === undefined) grouped.set(parentValue, [row]);
            else bucket.push(row);
        }
        const childrenByParent = new Map<string, UseCaseBreakdownRow[]>();
        for (const [parentValue, groupRows] of grouped) {
            const groupTokens = groupRows.reduce((sum, row) => sum + row.metrics.totalTokens, 0);
            childrenByParent.set(
                parentValue,
                groupRows.map((row) => {
                    const share = groupTokens > 0 ? row.metrics.totalTokens / groupTokens : 0;
                    return {
                        value: row.keys[childField] ?? '—',
                        parent: null,
                        parentValue,
                        turns: row.metrics.turns,
                        sessions: row.metrics.sessions,
                        users: row.metrics.users,
                        totalTokens: row.metrics.totalTokens,
                        allocatedCostUsd: row.metrics.allocatedCostUsd,
                        confidence: row.metrics.avgConfidence,
                        share,
                        widthPct: share > 0 ? Math.max(2, Math.round(share * 100)) : 0,
                    };
                }),
            );
        }
        return childrenByParent;
    }
}
